import type { Survey, SurveyPage, SurveyPhase, SpecialPage, ContentNavCta } from '../../fetchSurvey/types/survey';
import type { LogicContext } from '../../surveyLogic/types';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import { resolveEndPageId } from '../../surveyLogic/endPages/resolveEndPageId';
import { findLastVisiblePageIndex } from '../../surveyLogic/navigation/findVisiblePageIndex';

export function getSpecialPageForPhase(
  survey: Survey | null | undefined,
  phase: SurveyPhase
): SpecialPage | undefined {
  if (!survey) return undefined;

  switch (phase.kind) {
    case 'intro':
      return survey.introPage;
    case 'paused':
      return survey.pausedPage;
    case 'end': {
      const endPage = survey.endPages.find(p => p.id === phase.endPageId);
      return endPage ?? survey.endPages[0];
    }
    case 'submitted':
      return survey.submittedPage;
    case 'expired':
      return survey.expiredPage;
    case 'quota_fulfilled':
      return survey.quotaFulfilledPage;
    default:
      return undefined;
  }
}

export function computeCanGoBack(
  survey: Survey | null | undefined,
  phase: SurveyPhase,
  pageHistoryLength: number,
  currentPageIndex: number
): boolean {
  if (phase.kind !== 'content') return false;
  if (pageHistoryLength > 1) return true;
  if (currentPageIndex > 0) return true;
  return Boolean(survey?.introPage);
}

export function resolveContentNavCta(
  survey: Survey | null | undefined,
  currentPageIndex: number,
  primaryButtonType: 'next' | 'submit'
): ContentNavCta {
  const defaults = {
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    submitting: 'Submitting...',
  };

  if (!survey) {
    return {
      backLabel: defaults.back,
      primaryLabel: defaults.next,
      submittingLabel: defaults.submitting,
      backEnabled: true,
      primaryEnabled: true,
    };
  }

  const page = survey.pages[currentPageIndex];
  const backLabel = page?.cta?.previous?.label ?? defaults.back;
  const primaryLabel =
    primaryButtonType === 'submit'
      ? survey.cta?.submit?.label ?? page?.cta?.next?.label ?? defaults.submit
      : page?.cta?.next?.label ?? defaults.next;
  const backEnabled = page?.cta?.previous?.enabled !== false;
  const primaryEnabled =
    primaryButtonType === 'submit'
      ? survey.cta?.submit?.enabled !== false
      : page?.cta?.next?.enabled !== false;

  return {
    backLabel,
    primaryLabel,
    submittingLabel: defaults.submitting,
    backEnabled,
    primaryEnabled,
  };
}

export function computePrimaryButtonType(
  survey: Survey | null | undefined,
  phase: SurveyPhase,
  currentPageIndex: number,
  pages: SurveyPage[],
  context: LogicContext
): 'next' | 'submit' {
  if (phase.kind !== 'content' || !survey) return 'next';

  const lastVisible = findLastVisiblePageIndex(pages, context);
  if (lastVisible === -1) return 'submit';
  return currentPageIndex >= lastVisible ? 'submit' : 'next';
}

export function getDefaultEndPageId(
  survey: Survey | null | undefined,
  context?: LogicContext
): string | undefined {
  return resolveEndPageId(survey, context ?? buildLogicContext({}, []));
}
